import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Send, Loader2, Database, AlertCircle, Sparkles, Bot, Code, Copy, Check, ChevronDown, RotateCcw, Bookmark, GraduationCap, Zap } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { base44 } from '@/api/base44Client';
import ReactMarkdown from 'react-markdown';
import SaveFUQDialog from './SaveFUQDialog';
import FUQChips from './FUQChips';
import VariableForm from './VariableForm';
import { hasVariables } from './variableEngine';

const MODEL_OPTIONS = [
  { id: 'claude-sonnet-4-5-20250929', label: 'Sonnet 4.5' },
  { id: 'claude-opus-4-6', label: 'Opus 4.6' },
];
import ExportQueryToExcel from './ExportQueryToExcel';

export default function SqlChatAssistant({ open, onOpenChange }) {
  const [activeTab, setActiveTab] = useState('ask-data');
  const [guideMode, setGuideMode] = useState(false);
  const [guideStep, setGuideStep] = useState('start');
  const [customOptionInput, setCustomOptionInput] = useState({});
  const [askDataMessages, setAskDataMessages] = useState([]);
  const [sqlRunnerMessages, setSqlRunnerMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedSql, setCopiedSql] = useState(null);
  const [selectedModel, setSelectedModel] = useState(MODEL_OPTIONS[0].id);
  const [isCommanderInChief, setIsCommanderInChief] = useState(false);
  const [groupSelections, setGroupSelections] = useState({});
  const [loadingStage, setLoadingStage] = useState('');
  const [queryStartTime, setQueryStartTime] = useState(null);
  const [saveFuqOpen, setSaveFuqOpen] = useState(false);
  const [saveFuqQuery, setSaveFuqQuery] = useState('');
  const [saveFuqSource, setSaveFuqSource] = useState('ask_data');
  const [pendingVarSql, setPendingVarSql] = useState(null);
  const [lastVarTemplate, setLastVarTemplate] = useState(null);
  // Shape: { sql: "...{{vars}}...", context: 'sql_runner'|'ask_data' }
  const messagesEndRef = useRef(null);
  const loadingTimersRef = useRef([]);
  const sendingRef = useRef(false);

  // D: Restore conversation from sessionStorage on mount
  useEffect(() => {
    try {
      const savedAsk = sessionStorage.getItem('sentinel_askData');
      const savedSql = sessionStorage.getItem('sentinel_sqlRunner');
      if (savedAsk) {
        // Strip hasVariables from restored messages — VariableForm is session-only
        const parsed = JSON.parse(savedAsk);
        const cleaned = parsed.map(m => ({ ...m, hasVariables: false }));
        setAskDataMessages(cleaned);
      }
      if (savedSql) setSqlRunnerMessages(JSON.parse(savedSql));
      // Restore guide state
      const savedGuide = sessionStorage.getItem('sentinel_guideMode');
      const savedStep = sessionStorage.getItem('sentinel_guideStep');
      if (savedGuide === 'true') { setGuideMode(true); setGuideStep(savedStep || 'choosing_flow'); }
    } catch (e) { /* ignore parse errors */ }
  }, []);

  // D: Persist conversation to sessionStorage on change
  useEffect(() => {
    if (askDataMessages.length > 0) {
      try { sessionStorage.setItem('sentinel_askData', JSON.stringify(askDataMessages)); } catch(e) {}
    }
  }, [askDataMessages]);
  useEffect(() => {
    if (sqlRunnerMessages.length > 0) {
      try { sessionStorage.setItem('sentinel_sqlRunner', JSON.stringify(sqlRunnerMessages)); } catch(e) {}
    }
  }, [sqlRunnerMessages]);
  // Persist guide state
  useEffect(() => {
    try {
      sessionStorage.setItem('sentinel_guideMode', guideMode ? 'true' : 'false');
      sessionStorage.setItem('sentinel_guideStep', guideStep);
    } catch(e) {}
  }, [guideMode, guideStep]);

  // B: Staged loading helpers
  const startLoadingStages = (mode) => {
    // Clear any existing timers
    loadingTimersRef.current.forEach(t => clearTimeout(t));
    loadingTimersRef.current = [];
    if (mode === 'ask-data') {
      setLoadingStage('Analyzing your question...');
      loadingTimersRef.current.push(setTimeout(() => setLoadingStage('Loading database schema...'), 2000));
      loadingTimersRef.current.push(setTimeout(() => setLoadingStage('Generating SQL...'), 6000));
      loadingTimersRef.current.push(setTimeout(() => setLoadingStage('Running query against HexDB...'), 12000));
      loadingTimersRef.current.push(setTimeout(() => setLoadingStage('Still working — complex query...'), 25000));
    } else {
      setLoadingStage('Executing query...');
      loadingTimersRef.current.push(setTimeout(() => setLoadingStage('Still running...'), 10000));
      loadingTimersRef.current.push(setTimeout(() => setLoadingStage('Complex query — hang tight...'), 25000));
    }
  };
  const stopLoadingStages = () => {
    loadingTimersRef.current.forEach(t => clearTimeout(t));
    loadingTimersRef.current = [];
    setLoadingStage('');
  };

  const clearChat = () => {
    if (activeTab === 'ask-data') {
      setAskDataMessages([]);
      sessionStorage.removeItem('sentinel_askData');
    } else {
      setSqlRunnerMessages([]);
      sessionStorage.removeItem('sentinel_sqlRunner');
    }
    setInput('');
    setGroupSelections({});
    setPendingVarSql(null);
    setLastVarTemplate(null);
    setCustomOptionInput({});
    // Reset guide mode on clear
    if (activeTab === 'ask-data') {
      setGuideMode(false);
      setGuideStep('start');
      sessionStorage.removeItem('sentinel_guideMode');
      sessionStorage.removeItem('sentinel_guideStep');
    }
  };

  useEffect(() => {
    const checkRole = async () => {
      try {
        const user = await base44.auth.me();
        const profiles = await base44.entities.OfficerProfile.list();
        const profile = profiles.find(p => p.user_email === user?.email);
        setIsCommanderInChief(profile?.custom_role === 'Commander in Chief');
      } catch (e) { /* ignore */ }
    };
    if (open) checkRole();
  }, [open]);

  // Cleanup loading timers on unmount
  useEffect(() => {
    return () => { loadingTimersRef.current.forEach(t => clearTimeout(t)); };
  }, []);

  const messages = activeTab === 'ask-data' ? askDataMessages : sqlRunnerMessages;
  const setMessages = activeTab === 'ask-data' ? setAskDataMessages : setSqlRunnerMessages;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [askDataMessages, sqlRunnerMessages, activeTab]);

  // Build conversation history for the Sentinel Bot from ask-data messages
  const buildConversationHistory = () => {
    return askDataMessages
      .filter(m => m.sender === 'user' || m.sender === 'bot')
      .map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.rawBotResponse || m.content
      }));
  };

  const copySqlToRunner = (sql) => {
    setInput(sql);
    setActiveTab('sql-runner');
    setCopiedSql(sql);
    setTimeout(() => setCopiedSql(null), 2000);
  };

  const sendOption = (optionText) => {
    if (isLoading || sendingRef.current) return;
    sendingRef.current = true;

    // ── Client-side action intercepts (synchronous — reset ref on return) ──
    const lower = optionText.toLowerCase();

    // "Run with test values" / "Fill Variables" → show VariableForm
    if (lower.includes('run with test') || lower.includes('fill variable')) {
      const lastBotMsg = askDataMessages.filter(m => m.sender === 'bot' && m.variableTemplate).slice(-1)[0];
      if (lastBotMsg?.variableTemplate) {
        setPendingVarSql({ sql: lastBotMsg.variableTemplate, context: 'ask_data', botMessageId: lastBotMsg.id });
        sendingRef.current = false; return;
      }
    }

    // "Save as vFUQ" → open SaveFUQDialog with template
    if (lower.includes('save') && lower.includes('vfuq')) {
      const lastBotMsg = askDataMessages.filter(m => m.sender === 'bot' && m.variableTemplate).slice(-1)[0];
      if (lastBotMsg?.variableTemplate) {
        setSaveFuqQuery(lastBotMsg.variableTemplate);
        setSaveFuqSource('ask_data');
        setSaveFuqOpen(true);
        sendingRef.current = false; return;
      }
    }

    // "Save as reusable FUQ" / "Save as FUQ" / "Save this as a FUQ" → open SaveFUQDialog with resolved SQL
    if (lower.includes('save') && lower.includes('fuq') && !lower.includes('vfuq')) {
      const lastBotMsg = askDataMessages.filter(m => m.sender === 'bot' && m.sqlQuery).slice(-1)[0];
      if (lastBotMsg?.sqlQuery) {
        setSaveFuqQuery(lastBotMsg.sqlQuery);
        setSaveFuqSource('ask_data');
        setSaveFuqOpen(true);
        sendingRef.current = false; return;
      }
    }

    // "Exit guide" → turn off guide mode
    if (lower === 'exit guide') {
      setGuideMode(false);
      setGuideStep('start');
      setAskDataMessages(prev => [...prev, {
        id: Date.now(),
        sender: 'bot',
        type: 'bot-reply',
        mode: 'ask-data',
        content: '👋 Guide mode turned off. You can continue using Ask Data normally, or click **Guide** to start again anytime.',
        status: 'clarifying',
        options: []
      }]);
      sendingRef.current = false; return;
    }

    // "Run Query" / "Yes, run the query" → execute held SQL directly with confirmed_intent
    if (lower === 'run query' || lower === 'yes, run the query' || lower === 'yes, run it') {
      const lastBotMsg = askDataMessages.filter(m => m.sender === 'bot').slice(-1)[0];
      let heldSql = null;
      // Try to get SQL from the last bot message
      if (lastBotMsg?.sqlQuery) {
        heldSql = lastBotMsg.sqlQuery;
        console.log('[RUN_QUERY_INTERCEPT] Got SQL from sqlQuery, first 100:', heldSql.substring(0, 100));
      } else if (lastBotMsg?.rawBotResponse) {
        try {
          const raw = JSON.parse(lastBotMsg.rawBotResponse);
          heldSql = raw._held_sql || raw.sql_query || null;
          console.log('[RUN_QUERY_INTERCEPT] Got SQL from rawBotResponse._held_sql, first 100:', (heldSql || '').substring(0, 100));
        } catch(e) {
          console.log('[RUN_QUERY_INTERCEPT] Failed to parse rawBotResponse:', e.message);
        }
      }
      if (!heldSql) {
        console.log('[RUN_QUERY_INTERCEPT] No SQL found — falling through to LLM path');
      }
      console.log('[RUN_QUERY_INTERCEPT] lastBotMsg keys:', lastBotMsg ? Object.keys(lastBotMsg) : 'none');
      console.log('[RUN_QUERY_INTERCEPT] lastBotMsg.status:', lastBotMsg?.status);
      if (heldSql) {
        const userMessage = {
          id: Date.now(), sender: 'user', type: 'query', mode: 'ask-data', content: optionText
        };
        setAskDataMessages(prev => [...prev, userMessage]);
        setIsLoading(true);
        startLoadingStages('ask-data');
        const runStartTime = Date.now();
        const conversationHistory = [...askDataMessages, userMessage]
          .filter(m => m.sender === 'user' || m.sender === 'bot')
          .map(m => ({ role: m.sender === 'user' ? 'user' : 'assistant', content: m.rawBotResponse || m.content }));
        
        base44.functions.invoke('hexdbSentinelChat', {
          message: 'Run Query',
          conversation_history: conversationHistory,
          confirmed_intent: true,
          override_sql: heldSql,
          model: selectedModel,
          guide_mode: guideMode,
          guide_step: guideMode ? guideStep : undefined
        }).then(response => {
          const botData = response.data;
          if (botData.guide_step) setGuideStep(botData.guide_step);
          const botMsgId = Date.now() + 1;
          setAskDataMessages(prev => [...prev, {
            id: botMsgId,
            sender: 'bot',
            type: 'bot-reply',
            mode: 'ask-data',
            content: botData.reply || 'Query executed.',
            rawBotResponse: JSON.stringify(botData),
            sqlQuery: botData.sql_query || heldSql,
            queryResults: botData.query_results || null,
            queryError: botData.query_error || null,
            status: botData.status || 'results',
            options: botData.options || null,
            optionGroups: botData.option_groups || null,
            queryDuration: ((Date.now() - runStartTime) / 1000).toFixed(1),
            tokenInfo: botData.cache_info ? {
              input: botData.cache_info.input_tokens || 0,
              output: botData.cache_info.output_tokens || 0,
              cacheRead: botData.cache_info.cache_read_input_tokens || 0,
              cacheWrite: botData.cache_info.cache_creation_input_tokens || 0,
              tiers: botData.cache_info.tiers_loaded || [],
              domains: botData.cache_info.domains_detected || [],
              docSize: botData.cache_info.doc_assembled_length || 0
            } : null
          }]);
        }).catch(error => {
          const errorDetail = error.response?.data?.error || error.message || 'Failed to execute query';
          setAskDataMessages(prev => [...prev, {
            id: Date.now() + 1, sender: 'bot', type: 'error', mode: 'ask-data', content: errorDetail
          }]);
        }).finally(() => {
          setIsLoading(false);
          stopLoadingStages();
          sendingRef.current = false;
        });
        return; // Exit early — async handled above
      }
    }

    // "Show me the SQL first" → display SQL from last bot message without calling LLM
    if (lower.includes('show me the sql') || lower.includes('show sql')) {
      const lastSqlMsg = askDataMessages.filter(m => m.sender === 'bot' && m.sqlQuery).slice(-1)[0];
      const heldSql = lastSqlMsg?.sqlQuery || lastSqlMsg?.variableTemplate;
      if (heldSql) {
        setAskDataMessages(prev => [...prev,
          { id: Date.now(), sender: 'user', type: 'query', mode: 'ask-data', content: optionText },
          { id: Date.now() + 1, sender: 'bot', type: 'bot-reply', mode: 'ask-data',
            content: 'Here\'s the SQL query for review:\n\n```sql\n' + heldSql + '\n```\n\nReady to execute it, or want to modify it first?',
            sqlQuery: heldSql, status: 'confirming',
            variableTemplate: lastSqlMsg?.variableTemplate || null,
            hasVariables: lastSqlMsg?.hasVariables || false,
            options: lastSqlMsg?.hasVariables
              ? ['Save as vFUQ', 'Run with test values', 'Modify variables']
              : ['Run Query', 'Modify']
          }
        ]);
        sendingRef.current = false; return;
      }
    }

    // "Modify variables" → re-show VariableForm with the template
    if (lower.includes('modify var') || lower === 'modify variables') {
      const lastVarMsg = askDataMessages.filter(m => m.sender === 'bot' && m.variableTemplate).slice(-1)[0];
      if (lastVarMsg?.variableTemplate) {
        setPendingVarSql({ sql: lastVarMsg.variableTemplate, context: 'ask_data', botMessageId: lastVarMsg.id });
        sendingRef.current = false; return;
      }
    }

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      type: 'query',
      mode: 'ask-data',
      content: optionText
    };
    setAskDataMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    startLoadingStages('ask-data');
    setQueryStartTime(Date.now());

    const conversationHistory = [...askDataMessages, userMessage]
      .filter(m => m.sender === 'user' || m.sender === 'bot')
      .map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.rawBotResponse || m.content
      }));

    base44.functions.invoke('hexdbSentinelChat', {
      message: optionText,
      conversation_history: conversationHistory,
      model: selectedModel,
      guide_mode: guideMode,
      guide_step: guideMode ? guideStep : undefined
    }).then(response => {
      const botData = response.data;
      console.log('[SEND_OPTION] botData keys:', Object.keys(botData));
      console.log('[SEND_OPTION] _has_variables:', botData._has_variables);
      console.log('[SEND_OPTION] _variable_sql first 100:', (botData._variable_sql || '').substring(0, 100));
      console.log('[SEND_OPTION] sql_query exists:', !!botData.sql_query);
      console.log('[SEND_OPTION] status:', botData.status);
      console.log('[SEND_OPTION] options:', botData.options);
      if (botData.guide_step) setGuideStep(botData.guide_step);
      if (botData.error) {
        setAskDataMessages(prev => [...prev, {
          id: Date.now() + 1, sender: 'bot', type: 'error', mode: 'ask-data', content: botData.error
        }]);
      } else {
        const botMsgId = Date.now() + 1;
        // ── Frontend-side variable detection ──
        let soHasVars = botData._has_variables || false;
        let soVarSql = botData._variable_sql || null;
        if (!soHasVars) {
          const sqlToScan = botData._held_sql || botData.sql_query || '';
          if (sqlToScan.includes('{{')) {
            console.log('[SEND_OPTION] Frontend caught {{ in held/sql — forcing VariableForm');
            soHasVars = true;
            soVarSql = sqlToScan;
          }
        }
        setAskDataMessages(prev => [...prev, {
          id: botMsgId,
          sender: 'bot',
          type: 'bot-reply',
          mode: 'ask-data',
          content: botData.reply || 'I received your message.',
          rawBotResponse: JSON.stringify(botData),
          sqlQuery: botData.sql_query || null,
          queryResults: botData.query_results || null,
          queryError: botData.query_error || null,
          status: botData.status || 'clarifying',
          options: soHasVars && !guideMode ? ['Fill Variables'] : (botData.options || null),
          optionGroups: botData.option_groups || null,
          hasVariables: soHasVars,
          variableTemplate: soVarSql,
          queryDuration: queryStartTime ? ((Date.now() - queryStartTime) / 1000).toFixed(1) : null,
          tokenInfo: botData.cache_info ? {
            input: botData.cache_info.input_tokens || 0,
            output: botData.cache_info.output_tokens || 0,
            cacheRead: botData.cache_info.cache_read_input_tokens || 0,
            cacheWrite: botData.cache_info.cache_creation_input_tokens || 0,
            tiers: botData.cache_info.tiers_loaded || [],
            domains: botData.cache_info.domains_detected || [],
            docSize: botData.cache_info.doc_assembled_length || 0
          } : null
        }]);
        // In guide mode: don't auto-show VariableForm — user clicks "Run with test values"
        // In normal mode: auto-show VariableForm as before
        if (!guideMode && soHasVars && soVarSql) {
          setPendingVarSql({ sql: soVarSql, context: 'ask_data', botMessageId: botMsgId });
        }
      }
    }).catch(error => {
      const errorDetail = error.response?.data?.error || error.message || 'Failed to process request';
      setAskDataMessages(prev => [...prev, {
        id: Date.now() + 1, sender: 'bot', type: 'error', mode: 'ask-data', content: errorDetail
      }]);
    }).finally(() => {
      setIsLoading(false);
      stopLoadingStages();
      sendingRef.current = false;
    });
  };

  const handleGroupSelect = (messageId, groupIndex, option) => {
    setGroupSelections(prev => {
      const msg = prev[messageId] || {};
      const current = msg[groupIndex] || { chips: [], custom: '' };
      const chips = current.chips || [];
      const updated = chips.includes(option)
        ? chips.filter(c => c !== option)
        : [...chips, option];
      return { ...prev, [messageId]: { ...msg, [groupIndex]: { ...current, chips: updated } } };
    });
  };

  const handleGroupCustom = (messageId, groupIndex, text) => {
    setGroupSelections(prev => {
      const msg = prev[messageId] || {};
      const current = msg[groupIndex] || { chips: [], customs: [], currentCustom: '' };
      return { ...prev, [messageId]: { ...msg, [groupIndex]: { ...current, currentCustom: text } } };
    });
  };

  const confirmGroupCustom = (messageId, groupIndex) => {
    setGroupSelections(prev => {
      const msg = prev[messageId] || {};
      const current = msg[groupIndex] || { chips: [], customs: [], currentCustom: '' };
      const text = (current.currentCustom || '').trim();
      if (!text) return prev;
      return { ...prev, [messageId]: { ...msg, [groupIndex]: {
        ...current,
        customs: [...(current.customs || []), text],
        currentCustom: '',
        showCustom: true
      } } };
    });
  };

  const removeGroupCustom = (messageId, groupIndex, customIndex) => {
    setGroupSelections(prev => {
      const msg = prev[messageId] || {};
      const current = msg[groupIndex] || { chips: [], customs: [] };
      const newCustoms = [...(current.customs || [])];
      newCustoms.splice(customIndex, 1);
      return { ...prev, [messageId]: { ...msg, [groupIndex]: { ...current, customs: newCustoms } } };
    });
  };

  const sendGroupedSelections = (messageId, groups) => {
    const selections = groupSelections[messageId] || {};
    const parts = groups.map((group, i) => {
      const sel = selections[i] || { chips: [], customs: [] };
      const all = [...(sel.chips || []), ...(sel.customs || [])];
      return all.length > 0 ? `${group.question} ${all.join(', ')}` : null;
    }).filter(Boolean);
    if (parts.length === 0) return;
    const combined = parts.join('; ');
    setGroupSelections(prev => { const next = {...prev}; delete next[messageId]; return next; });
    sendOption(combined);
  };

  const countGroupSelections = (messageId, groups) => {
    const selections = groupSelections[messageId] || {};
    return groups.filter((_, i) => {
      const sel = selections[i] || { chips: [], customs: [] };
      return (sel.chips?.length > 0) || (sel.customs?.length > 0);
    }).length;
  };

  const handleAskDataVariableExecute = async (resolvedSQL) => {
    const executedTemplate = pendingVarSql?.sql || null;
    const safeSql = typeof resolvedSQL === 'string' ? resolvedSQL : String(resolvedSQL || '');
    
    // Debug: log what's being sent
    console.log('[VAR_EXECUTE] resolvedSQL type:', typeof resolvedSQL);
    console.log('[VAR_EXECUTE] safeSql first 200:', safeSql.substring(0, 200));
    console.log('[VAR_EXECUTE] has {{:', safeSql.includes('{{'));
    console.log('[VAR_EXECUTE] template first 200:', (executedTemplate || '').substring(0, 200));
    
    if (safeSql.includes('[object Object]') || !safeSql.trim()) {
      setAskDataMessages(prev => [...prev, {
        id: Date.now() + 1, sender: 'bot', type: 'error', mode: 'ask-data',
        content: 'Variable substitution failed — the query template may be malformed. Try regenerating it.'
      }]);
      return;
    }
    
    // Frontend guard: if resolved SQL still has {{ variables, don't send to backend
    if (safeSql.includes('{{')) {
      console.error('[VAR_EXECUTE] BLOCKED — resolved SQL still contains {{. Template:', executedTemplate?.substring(0, 100));
      setAskDataMessages(prev => [...prev, {
        id: Date.now() + 1, sender: 'bot', type: 'error', mode: 'ask-data',
        content: 'Some variables were not substituted. Please fill in all fields and try again.'
      }]);
      return;
    }
    setPendingVarSql(null);
    setIsLoading(true);
    startLoadingStages('ask-data');
    setQueryStartTime(Date.now());

    try {
      const conversationHistory = buildConversationHistory();
      const response = await base44.functions.invoke('hexdbSentinelChat', {
        message: 'Run Query',
        conversation_history: conversationHistory,
        confirmed_intent: true,
        override_sql: safeSql,
        model: selectedModel,
        guide_mode: guideMode,
        guide_step: guideMode ? guideStep : undefined
      });

      const botData = response.data;
      if (botData.guide_step) setGuideStep(botData.guide_step);
      if (botData.error) {
        setAskDataMessages(prev => [...prev, {
          id: Date.now() + 1, sender: 'bot', type: 'error', mode: 'ask-data', content: botData.error
        }]);
      } else {
        setAskDataMessages(prev => [...prev, {
          id: Date.now() + 1,
          sender: 'bot',
          type: 'bot-reply',
          mode: 'ask-data',
          content: botData.reply || 'Query executed.',
          rawBotResponse: JSON.stringify(botData),
          sqlQuery: botData.sql_query || safeSql,
          variableTemplate: executedTemplate,
          queryResults: botData.query_results || null,
          queryError: botData.query_error || null,
          status: botData.status || 'results',
          options: botData.options || null,
          optionGroups: botData.option_groups || null,
          queryDuration: queryStartTime ? ((Date.now() - queryStartTime) / 1000).toFixed(1) : null,
          tokenInfo: botData.cache_info ? {
            input: botData.cache_info.input_tokens || 0,
            output: botData.cache_info.output_tokens || 0,
            cacheRead: botData.cache_info.cache_read_input_tokens || 0,
            cacheWrite: botData.cache_info.cache_creation_input_tokens || 0,
            tiers: botData.cache_info.tiers_loaded || [],
            domains: botData.cache_info.domains_detected || [],
            docSize: botData.cache_info.doc_assembled_length || 0
          } : null
        }]);
      }
    } catch (error) {
      const errorDetail = error.response?.data?.error || error.message || 'Failed to process request';
      setAskDataMessages(prev => [...prev, {
        id: Date.now() + 1, sender: 'bot', type: 'error', mode: 'ask-data', content: errorDetail
      }]);
    } finally {
      setIsLoading(false);
      stopLoadingStages();
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    // SQL Runner: detect {{variables}} before sending
    if (activeTab === 'sql-runner' && hasVariables(input.trim())) {
      setPendingVarSql({ sql: input.trim(), context: 'sql_runner' });
      return;
    }

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      type: 'query',
      mode: activeTab,
      content: input.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    startLoadingStages(activeTab);
    setQueryStartTime(Date.now());

    try {
      if (activeTab === 'ask-data') {
        // Conversational mode via HexDB Sentinel Bot
        const conversationHistory = buildConversationHistory();
        
        const response = await base44.functions.invoke('hexdbSentinelChat', {
          message: userMessage.content,
          conversation_history: conversationHistory,
          model: selectedModel,
          guide_mode: guideMode,
          guide_step: guideMode ? guideStep : undefined
        });

        const botData = response.data;
        console.log('[HANDLE_SEND] botData keys:', Object.keys(botData));
        console.log('[HANDLE_SEND] _has_variables:', botData._has_variables);
        console.log('[HANDLE_SEND] _variable_sql first 100:', (botData._variable_sql || '').substring(0, 100));
        console.log('[HANDLE_SEND] sql_query exists:', !!botData.sql_query);
        console.log('[HANDLE_SEND] status:', botData.status);
        console.log('[HANDLE_SEND] options:', botData.options);
        console.log('[HANDLE_SEND] reply first 200:', (botData.reply || '').substring(0, 200));
        console.log('[HANDLE_SEND] reply has {{:', (botData.reply || '').includes('{{'));
        console.log('[HANDLE_SEND] _held_sql has {{:', (botData._held_sql || '').includes('{{'));
        console.log('[HANDLE_SEND] _held_sql first 100:', (botData._held_sql || '').substring(0, 100));
        console.log('[HANDLE_SEND] _held_sql last 100:', (botData._held_sql || '').slice(-100));
        if (botData.guide_step) setGuideStep(botData.guide_step);
        
        if (botData.error) {
          setAskDataMessages(prev => [...prev, {
            id: Date.now() + 1,
            sender: 'bot',
            type: 'error',
            mode: 'ask-data',
            content: botData.error
          }]);
          setIsLoading(false);
          stopLoadingStages();
          return;
        }

        // ── Frontend-side variable detection ──
        // If backend missed {{ in _held_sql or sql_query, detect it here
        let feHasVars = botData._has_variables || false;
        let feVarSql = botData._variable_sql || null;
        if (!feHasVars) {
          const sqlToScan = botData._held_sql || botData.sql_query || '';
          if (sqlToScan.includes('{{')) {
            console.log('[HANDLE_SEND] Frontend caught {{ in held/sql — forcing VariableForm');
            feHasVars = true;
            feVarSql = sqlToScan;
          }
        }

        // Build the bot message
        const botMessage = {
          id: Date.now() + 1,
          sender: 'bot',
          type: 'bot-reply',
          mode: 'ask-data',
          content: botData.reply || 'I received your message.',
          rawBotResponse: JSON.stringify(botData),
          sqlQuery: botData.sql_query || null,
          queryResults: botData.query_results || null,
          queryError: botData.query_error || null,
          status: botData.status || 'clarifying',
          options: feHasVars && !guideMode ? ['Fill Variables'] : (botData.options || null),
          optionGroups: botData.option_groups || null,
          hasVariables: feHasVars,
          variableTemplate: feVarSql,
          queryDuration: queryStartTime ? ((Date.now() - queryStartTime) / 1000).toFixed(1) : null,
          tokenInfo: botData.cache_info ? {
            input: botData.cache_info.input_tokens || 0,
            output: botData.cache_info.output_tokens || 0,
            cacheRead: botData.cache_info.cache_read_input_tokens || 0,
            cacheWrite: botData.cache_info.cache_creation_input_tokens || 0,
            tiers: botData.cache_info.tiers_loaded || [],
            domains: botData.cache_info.domains_detected || [],
            docSize: botData.cache_info.doc_assembled_length || 0
          } : null
        };

        setAskDataMessages(prev => [...prev, botMessage]);

        // In guide mode: don't auto-show VariableForm — user clicks "Run with test values"
        // In normal mode: auto-show VariableForm as before
        if (!guideMode && feHasVars && feVarSql) {
          setPendingVarSql({ sql: feVarSql, context: 'ask_data', botMessageId: botMessage.id });
        }

      } else {
        // SQL Runner Mode: Raw SQL query
        let responseData;
        try {
          const response = await base44.functions.invoke('invokeWebhook', { 
            webhook_type: 'sql_runner', 
            payload: { query: userMessage.content } 
          });
          responseData = response.data || response;
        } catch (axiosErr) {
          // Axios throws on non-2xx — extract the real error from the response body
          const errData = axiosErr.response?.data;
          const errorMsg = errData?.error || errData?.message || errData?.detail
            || (typeof errData === 'string' ? errData : null)
            || axiosErr.message || 'Failed to execute query';
          setSqlRunnerMessages(prev => [...prev, {
            id: Date.now() + 1,
            sender: 'system',
            type: 'error',
            mode: 'sql-runner',
            content: errorMsg
          }]);
          setIsLoading(false);
          stopLoadingStages();
          return;
        }

        if (responseData.error) {
          setSqlRunnerMessages(prev => [...prev, {
            id: Date.now() + 1,
            sender: 'system',
            type: 'error',
            mode: 'sql-runner',
            content: responseData.error
          }]);
        } else {
          const data = responseData.data;
          let contentToDisplay;
          let messageType;

          if (Array.isArray(data) && data.length === 1 && typeof data[0] === 'object') {
            const keys = Object.keys(data[0]);
            if (keys.length === 1) {
              const rawVal = data[0][keys[0]];
              contentToDisplay = typeof rawVal === 'number' && isFinite(rawVal)
                ? rawVal.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(rawVal) ? 0 : 2, maximumFractionDigits: 2 })
                : String(rawVal);
              messageType = 'text';
            } else {
              contentToDisplay = data;
              messageType = 'data';
            }
          } else if (Array.isArray(data) && data.length > 0) {
            contentToDisplay = data;
            messageType = 'data';
          } else if (data !== undefined && data !== null) {
            contentToDisplay = String(data);
            messageType = 'text';
          } else {
            contentToDisplay = responseData.response || responseData.message || JSON.stringify(responseData, null, 2);
            messageType = 'text';
          }

          setSqlRunnerMessages(prev => [...prev, {
            id: Date.now() + 1,
            sender: 'system',
            type: messageType,
            mode: 'sql-runner',
            content: contentToDisplay
          }]);
        }
      }
    } catch (error) {
      // Extract detailed error from Axios response if available
      const errorDetail = error.response?.data?.error 
        || error.response?.data?.message 
        || error.response?.data?.detail
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || error.message 
        || 'Failed to process request';
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        sender: activeTab === 'ask-data' ? 'bot' : 'system',
        type: 'error',
        mode: activeTab,
        content: errorDetail
      }]);
    } finally {
      setIsLoading(false);
      stopLoadingStages();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number' && isFinite(value)) {
      // Format numbers with commas; preserve decimal places if present
      return value.toLocaleString('en-US', {
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2
      });
    }
    // Check if string looks like a plain number (not an ID/code)
    if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim()) && value.trim().length <= 15) {
      const num = parseFloat(value.trim());
      if (isFinite(num) && Math.abs(num) >= 1000) {
        return num.toLocaleString('en-US', {
          minimumFractionDigits: value.includes('.') ? 2 : 0,
          maximumFractionDigits: 2
        });
      }
    }
    return String(value);
  };

  const renderDataTable = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return <div className="text-xs text-slate-400 italic">No data returned</div>;
    }

    const columns = Object.keys(data[0]);

    return (
      <div className="max-h-[400px] overflow-auto border border-slate-700 rounded-lg">
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow className="bg-slate-800/50">
              {columns.map((col, idx) => (
                <TableHead key={idx} className="text-slate-300 font-semibold text-xs">
                  {col}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, rowIdx) => (
              <TableRow key={rowIdx} className="hover:bg-slate-800/30">
                {columns.map((col, colIdx) => (
                  <TableCell key={colIdx} className="text-slate-400 text-xs">
                    {formatCellValue(row[col])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Render SQL with highlighted {{variables}}
  const renderHighlightedSQL = (sql) => {
    if (!sql) return null;
    const VAR_RE = /(\{\{[a-zA-Z_][a-zA-Z0-9_]*(?::[a-z]+)?(?:=[^}]*)?\}\})/g;
    const parts = sql.split(VAR_RE);
    return parts.map((part, i) => {
      if (VAR_RE.test(part)) {
        return (
          <span
            key={i}
            className="bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded px-1 py-0.5 font-semibold"
          >
            {part}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  // Color-coded chip styles for guide mode
  const getChipStyle = (option, canInteract) => {
    if (!canInteract) return 'border border-slate-700/50 text-slate-600 cursor-default';
    const lower = option.toLowerCase();
    // Purple for vFUQ actions
    if (lower.includes('vfuq')) return 'border border-purple-500/40 text-purple-300 hover:text-purple-100 hover:border-purple-400 hover:bg-purple-500/10 cursor-pointer';
    // Muted for exit
    if (lower === 'exit guide') return 'border border-slate-600/40 text-slate-400 hover:text-slate-200 hover:border-slate-500 hover:bg-slate-500/10 cursor-pointer';
    // Default emerald
    return 'border border-emerald-500/40 text-emerald-300 hover:text-emerald-100 hover:border-emerald-400 hover:bg-emerald-500/10 cursor-pointer';
  };

  // Custom option input helper
  const renderCustomChip = (messageId, canInteract) => {
    if (!canInteract) return null;
    const state = customOptionInput[messageId];
    if (state?.show) {
      return (
        <div className="flex items-center gap-1 border border-cyan-500/40 rounded-full px-2 py-0.5 bg-cyan-500/5">
          <input data-testid="sentinel-chat-input"
            type="text"
            value={state.text || ''}
            onChange={(e) => setCustomOptionInput(prev => ({
              ...prev, [messageId]: { show: true, text: e.target.value }
            }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const val = (state.text || '').trim();
                if (val) { sendOption(val); setCustomOptionInput(prev => ({ ...prev, [messageId]: { show: false, text: '' } })); }
              }
              if (e.key === 'Escape') setCustomOptionInput(prev => ({ ...prev, [messageId]: { show: false, text: '' } }));
            }}
            onBlur={() => {
              const val = (state.text || '').trim();
              if (val) sendOption(val);
              setCustomOptionInput(prev => ({ ...prev, [messageId]: { show: false, text: '' } }));
            }}
            placeholder="Type your own..."
            autoFocus
            className="bg-transparent border-none outline-none text-xs text-cyan-200 placeholder:text-slate-500 w-36"
          />
        </div>
      );
    }
    return (
      <button
        onClick={() => setCustomOptionInput(prev => ({ ...prev, [messageId]: { show: true, text: '' } }))}
        className="px-3 py-1.5 rounded-full text-xs border border-dashed border-cyan-500/30 text-cyan-400 hover:border-cyan-400 hover:bg-cyan-500/5 transition-all"
      >
        + Custom
      </button>
    );
  };

  const renderBotMessage = (message, isLastBotMessage = false) => {
    const canInteract = isLastBotMessage && !isLoading;

    return (
      <div className="space-y-3">
        {/* Status indicator */}
        <div className="flex items-center gap-2 mb-1">
          <Bot className="w-4 h-4 text-emerald-400" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
            HexDB Sentinel
          </span>
          {message.status === 'confirming' && (
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px]">Awaiting Confirmation</Badge>
          )}
          {message.status === 'results' && (
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">Query Complete</Badge>
          )}
          {message.status === 'executing' && (
            <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 text-[10px]">Running Query</Badge>
          )}
        </div>

        {/* Reply text */}
        <div className="text-sm prose prose-invert prose-sm max-w-none">
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </div>

        {/* Grouped option chips (multi-question, multi-select) */}
        {message.optionGroups && Array.isArray(message.optionGroups) && message.optionGroups.length > 0 && (
          <div className="space-y-3 mt-3">
            {message.optionGroups.map((group, gi) => {
              const sel = (groupSelections[message.id] || {})[gi] || { chips: [], customs: [], currentCustom: '' };
              const selectedChips = sel.chips || [];
              const customs = sel.customs || [];
              return (
                <div key={gi}>
                  <div className="text-xs text-slate-400 mb-1.5 font-medium">{group.question}</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {group.options.map((option, oi) => (
                      <button
                        key={oi}
                        onClick={() => canInteract && handleGroupSelect(message.id, gi, option)}
                        disabled={!canInteract}
                        className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                          selectedChips.includes(option)
                            ? 'bg-emerald-500/20 border border-emerald-400 text-emerald-200'
                            : canInteract
                            ? 'border border-slate-600 text-slate-400 hover:text-emerald-300 hover:border-emerald-500/40 hover:bg-emerald-500/5'
                            : 'border border-slate-700/50 text-slate-600 cursor-default'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                    {/* Confirmed custom entries as chips */}
                    {customs.map((c, ci) => (
                      <span key={`custom-${ci}`} className="px-3 py-1.5 rounded-full text-xs bg-cyan-500/20 border border-cyan-400 text-cyan-200 flex items-center gap-1.5">
                        {c}
                        {canInteract && (
                          <button onClick={() => removeGroupCustom(message.id, gi, ci)} className="text-cyan-400 hover:text-cyan-200">×</button>
                        )}
                      </span>
                    ))}
                    {/* Active custom input OR + Custom button */}
                    {canInteract && (
                      sel.showCustom ? (
                        <div className="flex items-center gap-1 border border-cyan-500/40 rounded-full px-2 py-0.5 bg-cyan-500/5">
                          <input data-testid="sentinel-sqlchatassistant-input-1"
                            type="text"
                            value={sel.currentCustom || ''}
                            onChange={(e) => handleGroupCustom(message.id, gi, e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                confirmGroupCustom(message.id, gi);
                              }
                            }}
                            onBlur={() => {
                              const val = (sel.currentCustom || '').trim();
                              if (val) {
                                confirmGroupCustom(message.id, gi);
                              } else {
                                // Close input if empty
                                setGroupSelections(prev => {
                                  const msg = prev[message.id] || {};
                                  const current = msg[gi] || { chips: [], customs: [], currentCustom: '' };
                                  return { ...prev, [message.id]: { ...msg, [gi]: { ...current, showCustom: false, currentCustom: '' } } };
                                });
                              }
                            }}
                            placeholder="Type here..."
                            autoFocus
                            className="bg-transparent border-none outline-none text-xs text-cyan-200 placeholder:text-slate-500 w-28"
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => setGroupSelections(prev => {
                            const msg = prev[message.id] || {};
                            const current = msg[gi] || { chips: [], customs: [], currentCustom: '' };
                            return { ...prev, [message.id]: { ...msg, [gi]: { ...current, showCustom: true } } };
                          })}
                          className="px-3 py-1.5 rounded-full text-xs border border-dashed border-cyan-500/30 text-cyan-400 hover:border-cyan-400 hover:bg-cyan-500/5 transition-all"
                        >
                          + Custom
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
            {canInteract && (
              <button
                onClick={() => sendGroupedSelections(message.id, message.optionGroups)}
                disabled={countGroupSelections(message.id, message.optionGroups) === 0}
                className={`mt-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  countGroupSelections(message.id, message.optionGroups) > 0
                    ? 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-lg shadow-emerald-900/30'
                    : 'bg-slate-800/50 text-slate-600 border border-slate-700/50 cursor-default'
                }`}
              >
                <Sparkles className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                Send Selections ({countGroupSelections(message.id, message.optionGroups)}/{message.optionGroups.length})
              </button>
            )}
          </div>
        )}

        {/* Clickable option chips — render here ONLY when NOT a results message */}
        {message.options && Array.isArray(message.options) && message.options.length > 0
          && message.status !== 'results'
        && (
          <div className="flex flex-wrap gap-2 items-center mt-2">
            {message.status === 'confirming' ? (
              message.options.map((option, i) => {
                const lower = option.toLowerCase();
                const isConfirm = lower.includes('run') || lower === 'yes' || lower.includes('yes,') || lower.includes('go ahead') || lower.includes('execute');
                return (
                  <button
                    key={i}
                    onClick={() => canInteract && sendOption(isConfirm ? 'Yes, run the query' : option)}
                    disabled={!canInteract}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      canInteract
                        ? isConfirm
                          ? 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-lg shadow-emerald-900/30'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-slate-100 border border-slate-600'
                        : 'bg-slate-800/50 text-slate-600 border border-slate-700/50 cursor-default'
                    }`}
                  >
                    {isConfirm && <Sparkles className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                    {option}
                  </button>
                );
              })
            ) : (
              message.options.map((option, i) => (
                <button
                  key={i}
                  onClick={() => canInteract && sendOption(option)}
                  disabled={!canInteract}
                  className={`px-3 py-1.5 rounded-full text-xs transition-all ${guideMode ? getChipStyle(option, canInteract) : (
                    canInteract
                      ? 'border border-emerald-500/40 text-emerald-300 hover:text-emerald-100 hover:border-emerald-400 hover:bg-emerald-500/10 cursor-pointer'
                      : 'border border-slate-700/50 text-slate-600 cursor-default'
                  )}`}
                >
                  {option}
                </button>
              ))
            )}
            {renderCustomChip(message.id, canInteract)}
          </div>
        )}

        {/* SQL Query block */}
        {message.sqlQuery && (
          <div className="mt-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 bg-cyan-500/10 border-b border-cyan-500/20">
              <div className="flex items-center gap-1.5 text-xs text-cyan-300">
                <Code className="w-3 h-3" />
                SQL Query
              </div>
              <div className="flex items-center gap-1">
                {/* Save as vFUQ — when variable template is available */}
                {(message.variableTemplate || hasVariables(message.sqlQuery)) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSaveFuqQuery(message.variableTemplate || message.sqlQuery);
                      setSaveFuqSource('ask_data');
                      setSaveFuqOpen(true);
                    }}
                    className="h-6 px-2 text-[10px] text-purple-300 hover:text-purple-100 hover:bg-purple-500/20"
                  >
                    <Bookmark className="w-3 h-3 mr-1" />
                    Save as vFUQ
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSaveFuqQuery(message.sqlQuery);
                    setSaveFuqSource('ask_data');
                    setSaveFuqOpen(true);
                  }}
                  className="h-6 px-2 text-[10px] text-amber-300 hover:text-amber-100 hover:bg-amber-500/20"
                >
                  <Bookmark className="w-3 h-3 mr-1" />
                  Save as FUQ
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copySqlToRunner(message.sqlQuery)}
                  className="h-6 px-2 text-[10px] text-cyan-300 hover:text-cyan-100 hover:bg-cyan-500/20"
                >
                  {copiedSql === message.sqlQuery ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                  {copiedSql === message.sqlQuery ? 'Copied to SQL Runner' : 'Copy to SQL Runner'}
                </Button>
              </div>
            </div>
            <pre className="p-3 text-xs font-mono text-cyan-200 whitespace-pre-wrap overflow-x-auto">
              {renderHighlightedSQL(message.sqlQuery)}
            </pre>
          </div>
        )}

        {/* Inline Variable Form — shown when bot returns SQL with unresolved variables */}
        {message.hasVariables && pendingVarSql?.botMessageId === message.id && (
          <div className="mt-2">
            {guideMode && (
              <div className="mb-2 px-3 py-2 rounded-lg bg-purple-500/5 border border-purple-500/20 text-xs text-purple-300/80">
                <GraduationCap className="w-3 h-3 inline mr-1.5 -mt-0.5 text-purple-400" />
                <span className="font-semibold">Try it!</span> Fill in the values below and click <span className="font-semibold text-emerald-300">Run Query</span> to see your dynamic query in action.
              </div>
            )}
            <VariableForm
              sqlTemplate={pendingVarSql.sql}
              onExecute={handleAskDataVariableExecute}
              onCancel={() => setPendingVarSql(null)}
            />
          </div>
        )}

        {/* Query Results table */}
        {message.queryResults && Array.isArray(message.queryResults) && message.queryResults.length > 0 && (
          <div className="mt-2">
            <div className="text-xs text-slate-400 mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Database className="w-3 h-3" />
                  Query Results ({message.queryResults.length} row{message.queryResults.length !== 1 ? 's' : ''})
                </div>
                {message.queryDuration && (
                  <Badge className="bg-slate-700/50 text-slate-400 border-slate-600 text-[10px] px-1.5 py-0">
                    {message.queryDuration}s
                  </Badge>
                )}
              </div>
              <ExportQueryToExcel data={message.queryResults} sqlQuery={message.sqlQuery} />
            </div>
            {renderDataTable(message.queryResults)}
          </div>
        )}

        {/* Post-results options — render AFTER results so user sees them at the bottom */}
        {message.options && Array.isArray(message.options) && message.options.length > 0
          && message.status === 'results'
        && (
          <div className="flex flex-wrap gap-2 items-center mt-3 pt-3 border-t border-slate-700/30">
            {message.options.map((option, i) => (
              <button
                key={i}
                onClick={() => canInteract && sendOption(option)}
                disabled={!canInteract}
                className={`px-3 py-1.5 rounded-full text-xs transition-all ${guideMode ? getChipStyle(option, canInteract) : (
                  canInteract
                    ? 'border border-emerald-500/40 text-emerald-300 hover:text-emerald-100 hover:border-emerald-400 hover:bg-emerald-500/10 cursor-pointer'
                    : 'border border-slate-700/50 text-slate-600 cursor-default'
                )}`}
              >
                {option}
              </button>
            ))}
            {renderCustomChip(message.id, canInteract)}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sentinel-sqlchatassistant-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-5xl w-[90vw] h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {activeTab === 'ask-data' ? (
                  <Bot className="w-5 h-5 text-emerald-400" />
                ) : (
                  <Database className="w-5 h-5 text-cyan-400" />
                )}
                HexDB Assistant
              </div>
              {isCommanderInChief && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1 px-2 py-1 rounded-md bg-slate-800 border border-slate-700 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors">
                      {MODEL_OPTIONS.find(m => m.id === selectedModel)?.label}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-slate-800 border-slate-700">
                    {MODEL_OPTIONS.map(model => (
                      <DropdownMenuItem
                        key={model.id}
                        onClick={() => setSelectedModel(model.id)}
                        className={`text-xs ${selectedModel === model.id ? 'text-emerald-300 bg-emerald-500/10' : 'text-slate-300'}`}
                      >
                        {model.label}
                        {selectedModel === model.id && <Check className="w-3 h-3 ml-2" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-slate-800 border border-slate-700">
                  <TabsTrigger value="ask-data" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
                    <Bot className="w-3 h-3 mr-1" />
                    Ask Data
                  </TabsTrigger>
                  <TabsTrigger value="sql-runner" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-300">
                    <Database className="w-3 h-3 mr-1" />
                    SQL Runner
                  </TabsTrigger>
                </TabsList>

                {/* Guide Mode */}
                <div className="flex items-center gap-1 ml-2">
                  {!guideMode ? (
                    <button
                      onClick={() => {
                        setGuideMode(true);
                        setGuideStep('choosing_flow');
                        setActiveTab('ask-data');
                        setAskDataMessages([{
                          id: Date.now(),
                          sender: 'bot',
                          type: 'bot-reply',
                          mode: 'ask-data',
                          content: "👋 **Guide Mode activated!** I'm your hands-on building assistant.\n\nI'll help you create queries, add variables, save FUQs, and generate integration code — step by step. What would you like to do?",
                          status: 'clarifying',
                          options: [
                            'Build a query from scratch',
                            'Add variables to a query',
                            'Show me how FUQs work',
                            'Generate integration code'
                          ]
                        }]);
                      }}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 border border-transparent hover:border-purple-500/20 transition-all"
                    >
                      <GraduationCap className="w-3.5 h-3.5" />
                      Guide
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/10 border border-purple-500/25">
                      <GraduationCap className="w-3 h-3 text-purple-400" />
                      <span className="text-[10px] font-semibold text-purple-300">Guide</span>
                      <button
                        onClick={() => { setGuideMode(false); setGuideStep('start'); }}
                        className="ml-1 text-purple-400/60 hover:text-purple-300 text-xs"
                        title="Turn off guide mode"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </Tabs>
              {messages.length > 0 && (
                <button
                  onClick={clearChat}
                  disabled={isLoading}
                  title="New chat"
                  className="p-1.5 rounded-md text-slate-500 hover:text-slate-200 hover:bg-slate-700/50 transition-colors disabled:opacity-30"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Messages Area */}
        <div className="flex-1 overflow-x-auto overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(100,116,139,0.15) transparent' }}>
          <div className="space-y-4 min-w-0">
            {messages.length === 0 && (
              <div className="text-center text-slate-500 mt-8">
                {activeTab === 'ask-data' ? (
                  <div className="space-y-3">
                    <Bot className="w-12 h-12 mx-auto opacity-50 text-emerald-400" />
                    <p className="text-sm font-medium text-slate-300">HexDB Sentinel Bot</p>
                    <p className="text-xs text-slate-500 max-w-md mx-auto">
                      I'll help you query HexDB. Tell me what data you're looking for in plain English — I'll refine my understanding, confirm with you, then generate and run the SQL query.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center mt-4">
                      {['Show me total sales this week', 'Which stores had the most orders yesterday?', 'Compare revenue across regions'].map((suggestion, i) => (
                        <button
                          key={i}
                          onClick={() => setInput(suggestion)}
                          className="px-3 py-1.5 rounded-full border border-slate-700 text-xs text-slate-400 hover:text-emerald-300 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <Database className="w-12 h-12 mx-auto mb-3 opacity-50 text-cyan-400" />
                    <p className="text-sm">Type a SQL query to execute directly</p>
                    <FUQChips onSelectQuery={(sql) => setInput(sql)} canEdit={isCommanderInChief} />
                  </>
                )}
              </div>
            )}

            {(() => {
              const lastBotMsg = messages.filter(m => m.sender === 'bot' && m.type === 'bot-reply').slice(-1)[0];
              return messages.map((message) => {
              const isLastBotMessage = lastBotMsg?.id === message.id;
              return (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-full w-full rounded-lg p-3 ${
                    message.sender === 'user'
                      ? message.mode === 'ask-data' 
                        ? 'bg-emerald-600 text-white'
                        : 'bg-indigo-600 text-white'
                      : message.type === 'error'
                      ? 'bg-red-900/30 border border-red-500/30 text-red-300'
                      : 'bg-slate-800 text-slate-200'
                  }`}
                >
                  {message.sender === 'user' ? (
                    <pre className={`text-sm whitespace-pre-wrap break-words ${message.mode === 'sql-runner' ? 'font-mono text-xs' : ''}`}>
                      {message.content}
                    </pre>
                  ) : message.type === 'error' ? (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{typeof message.content === 'string' ? message.content : 'An error occurred'}</span>
                    </div>
                  ) : message.type === 'bot-reply' ? (
                    renderBotMessage(message, isLastBotMessage)
                  ) : message.type === 'text' ? (
                    <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                  ) : (
                    <div>
                      <div className="text-xs text-slate-400 mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <Database className="w-3 h-3" />
                          Query Results ({message.content.length} rows)
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              // If query came from a variable template, save the template instead of resolved SQL
                              if (lastVarTemplate && lastVarTemplate.context === 'sql_runner') {
                                setSaveFuqQuery(lastVarTemplate.sql);
                              } else {
                                const lastUserQuery = messages.filter(m => m.sender === 'user').slice(-1)[0]?.content;
                                setSaveFuqQuery(lastUserQuery || '');
                              }
                              setSaveFuqSource('sql_runner');
                              setSaveFuqOpen(true);
                            }}
                            className="h-6 px-2 text-[10px] text-amber-300 hover:text-amber-100 hover:bg-amber-500/20"
                          >
                            <Bookmark className="w-3 h-3 mr-1" />
                            {lastVarTemplate && lastVarTemplate.context === 'sql_runner' ? 'Save Template as FUQ' : 'Save as FUQ'}
                          </Button>
                          <ExportQueryToExcel data={message.content} sqlQuery={messages.filter(m => m.sender === 'user').slice(-1)[0]?.content} />
                        </div>
                      </div>
                      {renderDataTable(message.content)}
                    </div>
                  )}
                  {/* Token consumption badge — collapsible */}
                  {message.sender === 'bot' && message.tokenInfo && (() => {
                    const ti = message.tokenInfo;
                    const totalInput = ti.input + ti.cacheRead + ti.cacheWrite;
                    const totalTokens = totalInput + ti.output;
                    const cachePct = totalInput > 0 ? Math.round(ti.cacheRead / totalInput * 100) : 0;
                    // Sonnet pricing: $3/MTok uncached input, $3.75/MTok cache write, $0.30/MTok cache read, $15/MTok output
                    const cost = (ti.input * 3 + ti.cacheWrite * 3.75 + ti.cacheRead * 0.30 + ti.output * 15) / 1_000_000;
                    return (
                    <div className="mt-2 pt-1.5 border-t border-slate-700/50">
                      <button
                        onClick={(e) => {
                          const detail = e.currentTarget.nextElementSibling;
                          if (detail) detail.classList.toggle('hidden');
                        }}
                        className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-slate-400 transition-colors"
                      >
                        <Zap className="w-2.5 h-2.5" />
                        {totalTokens.toLocaleString()} tokens
                        {cachePct > 0 && (
                          <span className="text-emerald-600 ml-1">({cachePct}% cached)</span>
                        )}
                        {message.queryDuration && <span>• {message.queryDuration}s</span>}
                        <span>• ${cost.toFixed(4)}</span>
                        <ChevronDown className="w-2.5 h-2.5" />
                      </button>
                      <div className="hidden mt-1 text-[9px] text-slate-600 space-y-0.5 pl-4">
                        <div>Input: {totalInput.toLocaleString()} (uncached: {ti.input.toLocaleString()}) • Output: {ti.output.toLocaleString()}</div>
                        {ti.cacheRead > 0 && <div>Cache read: {ti.cacheRead.toLocaleString()} • Cache write: {ti.cacheWrite.toLocaleString()}</div>}
                        {ti.tiers?.length > 0 && <div>Tiers: {ti.tiers.join(', ')}</div>}
                        {ti.domains?.length > 0 && <div>Domains: {ti.domains.join(', ')}</div>}
                        {ti.docSize > 0 && <div>Doc payload: {(ti.docSize / 1000).toFixed(1)}K chars</div>}
                        <div>Est. cost: ${cost.toFixed(4)} (uncached: ${(ti.input * 3 / 1_000_000).toFixed(4)} + cache read: ${(ti.cacheRead * 0.30 / 1_000_000).toFixed(4)} + cache write: ${(ti.cacheWrite * 3.75 / 1_000_000).toFixed(4)} + output: ${(ti.output * 15 / 1_000_000).toFixed(4)})</div>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              </div>
              );
            }); })()}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 text-slate-300 rounded-lg p-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">
                    {loadingStage || (activeTab === 'ask-data' ? 'Sentinel is thinking...' : 'Executing query...')}
                  </span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* FUQ Chips for SQL Runner (always visible when tab is sql-runner) */}
        {activeTab === 'sql-runner' && sqlRunnerMessages.length > 0 && (
          <div className="px-1 pt-2">
            <FUQChips onSelectQuery={(sql) => setInput(sql)} canEdit={isCommanderInChief} />
          </div>
        )}

        {/* Variable Form for SQL Runner */}
        {pendingVarSql && pendingVarSql.context === 'sql_runner' && (
          <div className="px-1 pt-2">
            <div className="flex items-center justify-between mb-1 px-1">
              <span className="text-[10px] text-slate-500">Fill variables then Run, or save the template as a reusable FUQ</span>
              <button
                onClick={() => {
                  setSaveFuqQuery(pendingVarSql.sql);
                  setSaveFuqSource('sql_runner');
                  setSaveFuqOpen(true);
                }}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-amber-300 hover:text-amber-100 hover:bg-amber-500/20 transition-colors"
              >
                <Bookmark className="w-3 h-3" />
                Save Template as FUQ
              </button>
            </div>
            <VariableForm
              sqlTemplate={pendingVarSql.sql}
              onExecute={(resolvedSQL) => {
                setLastVarTemplate({ sql: pendingVarSql.sql, context: 'sql_runner' });
                setPendingVarSql(null);
                setInput(resolvedSQL);
              }}
              onCancel={() => setPendingVarSql(null)}
            />
          </div>
        )}

        {/* Input Area */}
        <div className="border-t border-slate-800 pt-4 mt-2">
          <div className="flex items-stretch rounded-lg border border-slate-700 bg-slate-950 overflow-hidden focus-within:border-slate-600 transition-colors">
            <Textarea data-testid="sentinel-sqlchatassistant-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                activeTab === 'ask-data'
                  ? guideMode
                    ? 'Describe what you want to build... (Enter to send)'
                    : 'Ask HexDB Sentinel about your data... (Enter to send)'
                  : 'Enter SQL query... (Enter to send)'
              }
              className={`flex-1 bg-transparent text-slate-100 placeholder:text-slate-500 min-h-[44px] text-sm resize-none border-0 rounded-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 ${
                activeTab === 'sql-runner' ? 'font-mono' : ''
              }`}
              style={{ outline: 'none', boxShadow: 'none' }}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!input.trim() || isLoading}
              className={`px-4 flex items-center justify-center transition-colors disabled:opacity-40 ${activeTab === 'ask-data' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
        <SaveFUQDialog
          open={saveFuqOpen}
          onOpenChange={setSaveFuqOpen}
          sqlQuery={saveFuqQuery}
          sourceTab={saveFuqSource}
        />
      </DialogContent>
    </Dialog>
  );
}